import React, { useState } from 'react';
import { Settings, X, Play, RotateCcw, SkipForward, CheckCircle, Search, Eye, Copy, Lock, Clock, Home } from 'lucide-react';
import { MOCK_ANSWERS, isDevelopment } from '../utils/mockData';

export function DevTools({
  onFillMockData,
  onReset,
  onJumpToQuestion,
  onShowSuccess,
  onShowClosedScreen,
  onShowWaitingScreen,
  onBackToSurvey,
  questions,
  currentStep,
  answers
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [showQuestionList, setShowQuestionList] = useState(false);
  const [showAnswers, setShowAnswers] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [answerSearchTerm, setAnswerSearchTerm] = useState('');
  const [copiedAnswers, setCopiedAnswers] = useState(false);

  // Only show in development
  if (!isDevelopment()) {
    return null;
  }

  // Helper function to resolve options (handles both arrays and functions)
  const resolveOptions = (question) => {
    if (!question.options) return [];
    if (typeof question.options === 'function') {
      return question.options(answers);
    }
    return question.options;
  };

  // Helper function to format answer values based on question type
  const formatAnswerValue = (question, value) => {
    if (!value && value !== 0 && value !== false) {
      return <span className="text-gray-400 italic">No answer</span>;
    }

    // Resolve options (handles both static arrays and dynamic functions)
    const options = resolveOptions(question);

    switch (question.type) {
      case 'select':
      case 'radio':
        // Find the label for the selected value
        const selectedOption = options?.find(opt => opt.value === value);
        return selectedOption?.label || value;

      case 'checkbox':
        // Format checkbox selections
        if (value.selections && Array.isArray(value.selections)) {
          const labels = value.selections.map(sel => {
            const option = options?.find(opt => opt.value === sel);
            return option?.label || sel;
          });
          const formatted = labels.join(', ');
          if (value.otherText) {
            return `${formatted}, Outro: "${value.otherText}"`;
          }
          return formatted;
        }
        return JSON.stringify(value);

      case 'sortable':
        // Format sortable as ordered list
        if (value.order && Array.isArray(value.order)) {
          return (
            <ol className="list-decimal list-inside text-xs">
              {value.order.map((item, index) => {
                const option = options?.find(opt => opt.value === item);
                return (
                  <li key={index} className="text-gray-700">
                    {option?.label || item}
                  </li>
                );
              })}
            </ol>
          );
        }
        return JSON.stringify(value);

      case 'rating':
        // Format rating as list of statements with ratings
        if (typeof value === 'object') {
          return (
            <div className="space-y-1 text-xs">
              {Object.entries(value).map(([statementId, rating]) => {
                const statement = question.statements?.find(s => s.id === statementId);
                return (
                  <div key={statementId} className="flex justify-between items-start gap-2">
                    <span className="text-gray-700 flex-1">{statement?.text || statementId}:</span>
                    <span className="font-semibold text-purple-600">{rating}</span>
                  </div>
                );
              })}
            </div>
          );
        }
        return JSON.stringify(value);

      case 'number':
      case 'text':
      case 'email':
      case 'tel':
      case 'url':
      default:
        return String(value);
    }
  };

  // Copy all answers to clipboard
  const handleCopyAnswers = () => {
    const jsonString = JSON.stringify(answers, null, 2);
    navigator.clipboard.writeText(jsonString).then(() => {
      setCopiedAnswers(true);
      setTimeout(() => setCopiedAnswers(false), 2000);
    });
  };

  const filteredQuestions = questions.filter((q, index) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      q.question.toLowerCase().includes(searchLower) ||
      q.id.toLowerCase().includes(searchLower) ||
      index.toString().includes(searchLower)
    );
  });

  // Filter answered questions for the View Answers section
  const answeredQuestions = questions.filter(q => {
    const hasAnswer = answers[q.id] !== undefined && answers[q.id] !== null && answers[q.id] !== '';
    if (!hasAnswer) return false;

    const searchLower = answerSearchTerm.toLowerCase();
    if (!searchLower) return true;

    return (
      q.question.toLowerCase().includes(searchLower) ||
      q.id.toLowerCase().includes(searchLower)
    );
  });

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-4 right-4 bg-gradient-to-r from-amber-500 to-orange-600 text-white p-3 rounded-full shadow-lg hover:from-amber-600 hover:to-orange-700 transition z-50"
        title="Dev Tools"
      >
        {isOpen ? <X className="w-6 h-6" /> : <Settings className="w-6 h-6" />}
      </button>

      {/* Dev Tools Panel */}
      {isOpen && (
        <div className="fixed bottom-20 right-4 bg-white rounded-lg shadow-2xl border-2 border-amber-500 z-50 w-80 max-h-[80vh] overflow-hidden flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-lg font-bold text-amber-700">Dev Tools</h3>
          </div>

          <div className="overflow-y-auto flex-1 p-4">
            <div className="space-y-2">
              {/* Quick Actions */}
              <div className="mb-4">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Quick Actions</p>
                <button
                  onClick={() => {
                    onFillMockData(MOCK_ANSWERS);
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition text-sm"
                >
                  <Play className="w-4 h-4" />
                  Fill Mock Data
                </button>

                <button
                  onClick={() => {
                    onReset();
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition mt-2 text-sm"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset Survey
                </button>
              </div>

              {/* Preview Screens */}
              <div className="mb-4">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Preview Screens</p>
                <button
                  onClick={() => {
                    onShowSuccess();
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-lg hover:from-amber-500 hover:to-orange-600 transition text-sm"
                >
                  <CheckCircle className="w-4 h-4" />
                  Success Screen
                </button>

                <button
                  onClick={() => {
                    onShowClosedScreen();
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition mt-2 text-sm"
                >
                  <Lock className="w-4 h-4" />
                  Closed Screen
                </button>

                <button
                  onClick={() => {
                    onShowWaitingScreen();
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition mt-2 text-sm"
                >
                  <Clock className="w-4 h-4" />
                  Waiting Screen
                </button>

                {onBackToSurvey && (
                  <button
                    onClick={() => {
                      onBackToSurvey();
                      setIsOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition mt-2 text-sm"
                  >
                    <Home className="w-4 h-4" />
                    Back to Survey
                  </button>
                )}
              </div>

              {/* View Answers */}
              <div className="mb-4">
                <button
                  onClick={() => setShowAnswers(!showAnswers)}
                  className="w-full flex items-center justify-between px-4 py-2 bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 transition text-sm font-medium"
                >
                  <span className="flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    View Answers
                    <span className="text-xs bg-amber-300 px-2 py-0.5 rounded-full">
                      {Object.keys(answers || {}).length}
                    </span>
                  </span>
                  <span className="text-xs">
                    {showAnswers ? '▲' : '▼'}
                  </span>
                </button>

                {showAnswers && (
                  <div className="mt-2 border-2 border-amber-200 rounded-lg p-2 bg-amber-50">
                    {/* Search */}
                    <div className="relative mb-2">
                      <Search className="w-4 h-4 absolute left-2 top-2.5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search answers..."
                        value={answerSearchTerm}
                        onChange={(e) => setAnswerSearchTerm(e.target.value)}
                        className="w-full pl-8 pr-2 py-2 text-xs border border-gray-300 rounded focus:outline-none focus:border-amber-400"
                      />
                    </div>

                    {/* Copy All Button */}
                    <button
                      onClick={handleCopyAnswers}
                      className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition text-xs mb-2 ${
                        copiedAnswers
                          ? 'bg-green-500 text-white'
                          : 'bg-gradient-to-r from-amber-500 to-orange-600 text-white hover:from-amber-600 hover:to-orange-700'
                      }`}
                    >
                      <Copy className="w-3 h-3" />
                      {copiedAnswers ? 'Copied!' : 'Copy All Answers (JSON)'}
                    </button>

                    {/* Answers List */}
                    <div className="max-h-60 overflow-y-auto space-y-2">
                      {answeredQuestions.length === 0 ? (
                        <p className="text-xs text-gray-500 text-center py-4">
                          {Object.keys(answers || {}).length === 0
                            ? 'No answers yet'
                            : 'No matching answers'}
                        </p>
                      ) : (
                        answeredQuestions.map((question) => {
                          const answer = answers[question.id];
                          return (
                            <div
                              key={question.id}
                              className="bg-white p-2 rounded border border-amber-200"
                            >
                              <div className="text-xs text-gray-500 mb-1 font-mono">
                                {question.id}
                              </div>
                              <div className="text-xs text-gray-600 mb-1 font-medium">
                                {question.question}
                              </div>
                              <div className="text-sm text-gray-900 font-semibold">
                                {formatAnswerValue(question, answer)}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Jump to Question */}
              <div>
                <button
                  onClick={() => setShowQuestionList(!showQuestionList)}
                  className="w-full flex items-center justify-between px-4 py-2 bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 transition text-sm font-medium"
                >
                  <span className="flex items-center gap-2">
                    <SkipForward className="w-4 h-4" />
                    Jump to Question
                  </span>
                  <span className="text-xs">
                    {showQuestionList ? '▲' : '▼'}
                  </span>
                </button>

                {showQuestionList && (
                  <div className="mt-2 border-2 border-amber-200 rounded-lg p-2 bg-amber-50">
                    {/* Search */}
                    <div className="relative mb-2">
                      <Search className="w-4 h-4 absolute left-2 top-2.5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search questions..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-8 pr-2 py-2 text-xs border border-gray-300 rounded focus:outline-none focus:border-amber-400"
                      />
                    </div>

                    {/* Question List */}
                    <div className="max-h-60 overflow-y-auto space-y-1">
                      {filteredQuestions.map((q, index) => {
                        const actualIndex = questions.findIndex(question => question.id === q.id);
                        const isCurrent = actualIndex === currentStep;
                        return (
                          <button
                            key={q.id}
                            onClick={() => {
                              onJumpToQuestion(actualIndex);
                              setIsOpen(false);
                              setShowQuestionList(false);
                            }}
                            className={`w-full text-left px-2 py-1.5 rounded text-xs hover:bg-amber-200 transition ${
                              isCurrent ? 'bg-amber-300 font-semibold' : 'bg-white'
                            }`}
                          >
                            <div className="flex items-start gap-2">
                              <span className="text-amber-700 font-mono flex-shrink-0">#{actualIndex}</span>
                              <span className="text-gray-700 line-clamp-2">{q.question}</span>
                            </div>
                          </button>
                        );
                      })}
                      {filteredQuestions.length === 0 && (
                        <p className="text-xs text-gray-500 text-center py-2">No questions found</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="p-3 border-t border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-500">
              Current: Question #{currentStep} / {questions.length}
            </p>
          </div>
        </div>
      )}
    </>
  );
}
